import { useRef, useMemo, useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Map as LeafletMap, Marker as LeafletMarker, Icon } from "leaflet";
import bluePinPng from "leaflet/dist/images/marker-icon.png";
import redPinPng from "../assets/red_pin.png";

import allPin from "../assets/all_pin.png";
import condoPin from "../assets/condo_pin.png";
import coopPin from "../assets/coop_pin.png";
import detachedPin from "../assets/detached_pin.png";
import multiplexPin from "../assets/multiplex_pin.png";
import townhousePin from "../assets/townhouse_pin.png";
import semiPin from "../assets/semi_pin.png";

import { fetchBrokerageDetail } from "../api";
import { useParams, Link } from "react-router-dom";
import StarRating from "../components/StarRating";
import bgImage from "../assets/bg2.jpg";
import { useNavigate } from "react-router-dom";

function FitBounds({ markers }) {
  const map = useMap();

  useEffect(() => {
    if (markers.length === 0) return;
    const bounds = markers.map(({ lat, lng }) => [lat, lng]);
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [markers, map]);

  return null;
}

export default function BrokerageDetail() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const mapRef = useRef<LeafletMap | null>(null);
  const markerRefs = useRef(new Map());

  const [selectedId, setSelectedId] = useState(null);
  const blueIcon = useMemo(() => new Icon({ iconUrl: bluePinPng, iconSize:[25,41], iconAnchor:[12,41] }), []);
  const redIcon  = useMemo(() => new Icon({ iconUrl: redPinPng,  iconSize:[41,41], iconAnchor:[20,41] }), []);
  const pinIcons = useMemo(() => ({
    ALL:       new Icon({ iconUrl: allPin,       iconSize:[25,41], iconAnchor:[12,41] }),
    CONDO:     new Icon({ iconUrl: condoPin,     iconSize:[25,41], iconAnchor:[12,41] }),
    COOP:      new Icon({ iconUrl: coopPin,      iconSize:[25,41], iconAnchor:[12,41] }),
    DETACHED:  new Icon({ iconUrl: detachedPin,  iconSize:[25,41], iconAnchor:[12,41] }),
    MULTIPLEX: new Icon({ iconUrl: multiplexPin, iconSize:[25,41], iconAnchor:[12,41] }),
    TOWNHOUSE: new Icon({ iconUrl: townhousePin, iconSize:[25,41], iconAnchor:[12,41] }),
    SEMI:      new Icon({ iconUrl: semiPin,      iconSize:[25,41], iconAnchor:[12,41] }),
    DEFAULT:   new Icon({ iconUrl: redPinPng,    iconSize:[25,41], iconAnchor:[12,41] }),
  }), []);

  function getPinIcon(type) {
    if (!type) return pinIcons.ALL; // 기본값을 ALL로

    const key = String(type).toUpperCase().trim();

    const alias = {
      "SEMI-DETACHED": "SEMI",
      "SEMI DETACHED": "SEMI",
      "SEMI": "SEMI",

      "TOWN HOUSE": "TOWNHOUSE",
      "TOWN-HOUSE": "TOWNHOUSE",
      "TOWNHOUSE": "TOWNHOUSE",

      "MULTI-PLEX": "MULTIPLEX",
      "MULTIPLEX": "MULTIPLEX",

      "DETACHED": "DETACHED",

      "CONDO": "CONDO",

      "CO-OP": "COOP",
      "CO OP": "COOP",
      "COOP": "COOP",
      "CO-OP APARTMENT": "COOP",

      "ALL": "ALL",
    };

    const normalized = alias[key] || key;
    return pinIcons[normalized] || pinIcons.ALL; // 매칭 안되면 ALL 핀으로
  }

  function focusListingOnMap(listingId: string | number) {
    const marker = markerRefs.current.get(listingId);
    if (!marker) return;
    const latlng = marker.getLatLng();
    mapRef.current?.flyTo(latlng, Math.max(mapRef.current.getZoom(), 14), {
      duration: 0.5,
    });
    marker.openPopup();
    setSelectedId(listingId);
    document.getElementById("listings-map")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  useEffect(() => {
    fetchBrokerageDetail(id)
      .then(setDetail)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <span className="text-gray-500 text-lg">Loading...</span>
      </div>
    );

  if (!detail)
    return (
      <div className="text-center text-red-500 py-20 text-lg">
        Brokerage not found
      </div>
    );

  const validSold = detail.listings.filter(
    (l) =>
      typeof l.lat === "number" &&
      typeof l.lng === "number" &&
      l.status === "sold"
  );
  const validOpen = detail.listings.filter(
    (l) =>
      typeof l.lat === "number" &&
      typeof l.lng === "number" &&
      l.status === "open"
  );
  const validListings = [...validSold, ...validOpen];

  const avgLat = validListings.length
    ? validListings.reduce((sum, l) => sum + l.lat, 0) / validListings.length
    : 37.7749;
  const avgLng = validListings.length
    ? validListings.reduce((sum, l) => sum + l.lng, 0) / validListings.length
    : -122.4194;

  return (
    <div className="relative min-h-screen w-full flex justify-center">
      {/* Background image only */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "top",
          backgroundRepeat: "no-repeat",
          // opacity: 0.7,
        }}
      />

      {/* Foreground content */}
      <div className="relative z-10 w-full max-w-5xl space-y-4 py-8 px-4">
        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-3xl font-bold text-gray-700">{detail.name}</h2>
          <div className="mt-2 flex items-center gap-4">
            <span className="text-xl font-semibold text-gray-700">
              {Math.round(detail.score)}
              <span className="text-gray-500 text-base font-normal">/100</span>
            </span>
            <StarRating score={detail.score} />
          </div>
        </div>

        {/* Details */}
        <div className="bg-white/90 rounded-xl shadow-md p-6 space-y-2">
          <h3 className="text-xl font-semibold text-gray-700">Details</h3>
          <ul className="text-gray-600 space-y-1">
            <li>
              Total Sold Listings:{" "}
              <span className="font-medium">{detail.total_count}</span>
            </li>
            <li>
              Avg. Price Diff:{" "}
              <span className="font-medium">
                $
                {detail.avg_price_diff.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </li>
            <li>
              Avg. Closing Period:{" "}
              <span className="font-medium">
                {detail.avg_closing_period} days
              </span>
            </li>
          </ul>
        </div>

        {/* Map */}
        {validListings.length > 0 && (
          <div id="listings-map" className="bg-white/90 rounded-xl shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">
              Listings Map
            </h3>
            <div className="w-full h-96 rounded overflow-hidden">
              <MapContainer
                center={[avgLat, avgLng]}
                zoom={12}
                style={{ height: "100%", width: "100%" }}
                whenCreated={(map) => { mapRef.current = map; }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {validSold.map((listing) => (
                  <Marker
                    key={listing.id}
                    position={[listing.lat, listing.lng]}
                    icon={getPinIcon(listing.building_type)}
                    ref={(ref) => {
                      if (ref) markerRefs.current.set(listing.id, ref);
                      else markerRefs.current.delete(listing.id);
                    }}
                  >
                    <Popup>
                      <span className="text-red-600 font-semibold">SOLD</span>
                      <br />${Number(listing.price).toLocaleString()}
                      <br />[{listing.building_type}] {listing.address}
                    </Popup>
                  </Marker>
                ))}
                {validOpen.map((listing) => (
                  <Marker
                    key={listing.id}
                    position={[listing.lat, listing.lng]}
                    icon={getPinIcon(listing.building_type)}
                    ref={(ref) => {
                      if (ref) markerRefs.current.set(listing.id, ref);
                      else markerRefs.current.delete(listing.id);
                    }}
                  >
                    <Popup>
                      <span className="text-green-600 font-semibold">OPEN</span>
                      <br />${Number(listing.price).toLocaleString()}
                      <br />[{listing.building_type}] {listing.address}
                    </Popup>
                  </Marker>
                ))}
                {validOpen.map((listing) => (
                  <Marker
                    key={listing.id}
                    position={[listing.lat, listing.lng]}
                    icon={getPinIcon(listing.building_type)}
                  >
                    <Popup>
                      <span className="text-green-600 font-semibold">OPEN</span>
                      <br />${listing.price}
                      <br />
                      {listing.address}
                    </Popup>
                  </Marker>
                ))}
                <FitBounds markers={validListings} />
              </MapContainer>
            </div>
          </div>
        )}

        {/* Sold Listings */}
        <div className="bg-white/90 rounded-xl shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Sold Listings</h3>
          {detail.recent_sold.length > 0 ? (
            <ul className="space-y-2">
              {detail.recent_sold.map((l) => {
                const diff = l.sold_price - l.initial_price;
                const isPositive = diff >= 0;
                return (
                  <li
                    key={l.id}
                    onClick={() => focusListingOnMap(l.id)}
                    className={`rounded p-4 cursor-pointer transition hover:ring-2 hover:ring-indigo-200 ${
                      isPositive
                        ? "bg-green-50 border border-green-100 text-green-800"
                        : "bg-orange-50 border border-orange-100 text-orange-800"
                    } ${selectedId === l.id ? "ring-2 ring-indigo-300" : ""}`}
                    role="button"
                    aria-label={`Focus ${l.address} on map`}
                  >
                    <strong>
                      [{l.building_type}] {l.address}
                    </strong>
                    <br />
                    Sold at $
                    {l.sold_price.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    (initially asked $
                    {l.initial_price.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    )
                    <br />
                    Difference:{" "}
                    <strong>
                      <span className={isPositive ? "text-green-600" : "text-orange-600"}>
                        $
                        {Math.abs(diff).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </strong>{" "}
                    · {l.days_on_market} days ({l.listed_date} → {l.sold_date})
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-gray-500 italic">No recent sold listings available.</p>
          )}
        </div>

        <button
          onClick={() => navigate("/")}
          className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-600 transition font-medium"
        >
          ← Back to Ranking
        </button>
      </div>
    </div>
  );
}
